import { Box, Container, Link, Stack, styled } from '@mui/material';
import SectionTitle from '../../sections/landing/components/SectionTitle';
import SectionDescription from '../../sections/landing/components/SectionDescription';
import CardTitle from '../../sections/landing/components/CardTitle';
import { useTheme } from '@mui/material/styles';

const List = styled('ul')({
  margin: 0,
  padding: 0,
  listStylePosition: 'inside'
});

const privacyPolicy = [
  {
    title: 'Introduction',
    description: {
      text: 'Knacksters (“we,” “our,” or “us”) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share your personal information when you use our platform or services.'
    }
  },
  {
    title: 'Data We Collect',
    description: {
      title: 'We collect the following types of information:',
      list: [
        'Personal Information: Name, email address, billing information, and other details provided during registration.',
        'Usage Data: Details about how you use our platform, including pages visited, features used, and interactions.',
        'Cookies: Small files placed on your device to improve user experience and enable analytics.'
      ]
    }
  },
  {
    title: 'How We Use Your Data',
    description: {
      title: 'Your data is used to:',
      list: [
        'Provide and enhance our services.',
        'Communicate updates, offers, and service-related information.',
        'Ensure the security and integrity of our platform.'
      ]
    }
  },
  {
    title: 'Data Sharing',
    description: {
      title: 'We may share your data with:',
      list: [
        'Service Providers: Third-party vendors who assist us with operations such as payment processing and analytics.',
        'Compliance Requirements: Law enforcement or regulatory authorities when required by law.',
        'Business Transfers: In the event of a merger, acquisition, or sale of assets, your data may be transferred.'
      ]
    }
  },
  {
    title: 'Cookies and Tracking',
    description: {
      title: 'Knacksters uses cookies to:',
      list: ['Remember user preferences.', 'Conduct analytics and improve performance.'],
      text: 'You can disable cookies in your browser settings, though some features of the platform may be impacted.'
    }
  },
  {
    title: 'Data Security',
    description: {
      text: 'We implement robust security measures to protect your personal information. However, no method of transmission over the internet is entirely secure, and we cannot guarantee absolute security.'
    }
  },
  {
    title: 'International Data Transfers',
    description: {
      text: 'As a UK-based company serving users globally, your data may be transferred and processed in countries outside your residence. We comply with applicable data protection laws to ensure your data is secure.'
    }
  },
  {
    title: 'Your Rights',
    description: {
      title: 'You have the right to:',
      list: [
        'Access your personal data.',
        'Correct or update inaccurate information.',
        'Request the deletion of your data.',
        'Object to the processing of your data for marketing purposes.'
      ]
    }
  },
  {
    title: 'Retention of Data',
    description: {
      text: 'We retain your data for as long as necessary to provide our services or comply with legal obligations. After this period, data is securely deleted.'
    }
  },
  {
    title: 'Changes to This Policy',
    description: {
      text: 'Knacksters may update this Privacy Policy periodically. Significant changes will be communicated to users through the platform or email.'
    }
  },
  {
    title: 'Contact Information',
    description: {
      text: ['If you have questions or concerns about this Privacy Policy, please contact us at ', 'connect@knacksters.co', '.']
    }
  }
];

const PrivacyPolicy = () => {
  const theme = useTheme();

  const renderTextWithLink = (text: string | string[]) => {
    if (Array.isArray(text)) {
      return (
        <>
          {text[0]}
          <Link href={`mailto:${text[1]}`} className="link" underline="none">
            {text[1]}
          </Link>
          {text[2]}
        </>
      );
    }
    return text;
  };

  return (
    <main>
      <Box component="section" py={8}>
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <SectionTitle pb={4}>Privacy Policy</SectionTitle>
            {privacyPolicy.map((item, index) => (
              <Stack
                gap={1}
                key={index}
                sx={{
                  '& .link': {
                    color: theme.palette.grey[600],

                    '&:hover': {
                      color: theme.palette.primary[900]
                    }
                  }
                }}
              >
                <CardTitle>
                  {index + 1}. {item.title}
                </CardTitle>
                {item.description.title && (
                  <SectionDescription
                    as="p"
                    sx={{
                      margin: 0
                    }}
                  >
                    {item.description.title}
                  </SectionDescription>
                )}
                {item.description.list && (
                  <List>
                    {item.description.list.map((text, index) => (
                      <SectionDescription key={index} as="li">
                        {renderTextWithLink(text)}
                      </SectionDescription>
                    ))}
                  </List>
                )}
                {item.description.text && (
                  <SectionDescription
                    as="p"
                    sx={{
                      margin: 0
                    }}
                  >
                    {renderTextWithLink(item.description.text)}
                  </SectionDescription>
                )}
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>
    </main>
  );
};

export default PrivacyPolicy;
